/* eslint-disable import/no-extraneous-dependencies */
import { Popover, PopoverButton, Transition } from "@headlessui/react";
import { io, Socket } from "socket.io-client";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import {
  HissaReportType,
  PlatformError,
  ReportStatus,
} from "../../../types/common";
import { calculateDateDiffTillNow } from "../../../utils/date";
import {
  disconnectSocket,
  getSocketConnection,
} from "../../../utils/socketConnection";
import { useCompanyStore } from "../../../store";
import { queryClient } from "../../../queries";
import { useReportJobStore } from "../../../store/reportJobStore";
import { useBatchEventStore } from "../../../store/batchEventStore";

const INTERNAL_ERROR_PATTERNS = [
  /ECONNREFUSED/i,
  /ENOTFOUND/i,
  /ETIMEDOUT/i,
  /timeout/i,
  /prisma/i,
  /database/i,
  /sql/i,
  /connection\s+refused/i,
  /internal\s+server\s+error/i,
  /500/,
  /redis/i,
  /socket\s+hang\s+up/i,
  /connect\s+ETIMEDOUT/i,
  /Error Fetching Users/i,
  /at Object\./,
  /at async /,
];

const GENERIC_ERROR =
  "An unexpected error occurred. Please contact the support team or HR.";

function getUserFacingError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  if (INTERNAL_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
    return GENERIC_ERROR;
  }
  return message;
}

function NotificationCenter() {
  const [notifications, setNotifications] = useState<PlatformError[]>([]);
  const navigate = useNavigate();
  const { upsertReport } = useReportJobStore();
  const { triggerGrantsRefresh } = useBatchEventStore();
  const { companyData } = useCompanyStore();
  const [tab, setTab] = useState<"unread" | "read" | "all">("unread");
  const prevUnreadCount = useRef(notifications.length);
  const [bellAnimate, setBellAnimate] = useState(false);
  const companyId = useMemo(() => {
    if (companyData) return companyData.id;
    return undefined;
  }, [companyData]);

  const [progressByCompany, setProgressByCompany] = useState<
    Record<
      string,
      {
        batchId: string;
        batchType: string;
        total: number;
        processed: number;
        failed: number;
        message: string;
        lastUpdatedAt: number;
      } | null
    >
  >({});
  const [minimizedByCompany, setMinimizedByCompany] = useState<
    Record<string, boolean>
  >({});

  const cKey = companyId ?? "";
  const processedGrantLetters = progressByCompany[cKey] ?? null;
  const minimized = minimizedByCompany[cKey] ?? false;

  const setProcessedGrantLetters = (
    val:
      | {
          batchId: string;
          batchType: string;
          total: number;
          processed: number;
          failed: number;
          message: string;
          lastUpdatedAt: number;
        }
      | null
      | ((prev: typeof processedGrantLetters) => typeof processedGrantLetters)
  ) => {
    setProgressByCompany((prev) => ({
      ...prev,
      [cKey]: typeof val === "function" ? val(prev[cKey] ?? null) : val,
    }));
  };
  const setMinimized = (val: boolean | ((prev: boolean) => boolean)) => {
    setMinimizedByCompany((prev) => ({
      ...prev,
      [cKey]: typeof val === "function" ? val(prev[cKey] ?? false) : val,
    }));
  };

  const [ETA, setETA] = useState<string | null>(null);
  const [firstGrantProcessedAt, setFirstGrantProcessedAt] = useState<
    number | null
  >(null);
  const [selectedNotification, setSelectedNotification] =
    useState<PlatformError | null>(null);
  const clearProgressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    setNotifications([]);
    disconnectSocket();
    setBellAnimate(false);

    const socket = getSocketConnection();

    const handleUnread = (data: PlatformError[]) => {
      if (data.length > 0) {
        setBellAnimate(true);
      }
      const _data = data.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        }
        return 0;
      });
      setNotifications(_data);
    };
    const handleGrantUnread = (data: PlatformError[]) => {
      setBellAnimate(true);
      setNotifications((prev) => {
        const all = [...data, ...prev];
        const seen = new Set();
        return all
          .filter((n) => {
            if (!n.id || seen.has(n.id)) return false;
            seen.add(n.id);
            return true;
          })
          .sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              return (
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
              );
            }
            return 0;
          });
      });
    };
    const handleRead = (data: PlatformError[]) => setNotifications(data);
    const handleAll = (data: PlatformError[]) => setNotifications(data);

    const lsBatchKey = `hissa_batch_${companyId ?? ""}`;
    const storedBatchId = localStorage.getItem(lsBatchKey);
    if (storedBatchId) {
      socket.emit("get-batch-metrics", { batchId: storedBatchId });

      const staleTimer = setTimeout(() => {
        setProgressByCompany((prev) => {
          const current = prev[companyId ?? ""];
          if (current && current.batchId === storedBatchId) {
            localStorage.removeItem(lsBatchKey);
            return { ...prev, [companyId ?? ""]: null };
          }
          return prev;
        });
      }, 2 * 60 * 1000);
      (socket as any).__staleTimer = staleTimer;
    }

    socket.on("letter-metrics", (data) => {
      const {
        batchId,
        batchType,
        total,
        processed,
        failed,
        isComplete,
        origin,
      } = data;
      const lsBatchKey = `hissa_batch_${companyId ?? ""}`;
      const normalizedBatchType = (batchType ?? "").toLowerCase();
      const normalizedOrigin = (origin ?? "").toLowerCase();
      const isCredentialsBatch =
        normalizedBatchType.includes("credential") ||
        normalizedOrigin.includes("credential");
      const isStateChangeBatch =
        normalizedBatchType === "state-change" ||
        normalizedBatchType.includes("state");
      const isLetterBatch =
        normalizedBatchType === "letter" && !isCredentialsBatch;
      const isBatchComplete =
        !!isComplete ||
        (typeof total === "number" &&
          total > 0 &&
          (processed ?? 0) + (failed ?? 0) >= total);

      if (isBatchComplete) {
        localStorage.removeItem(lsBatchKey);
        clearTimeout((socket as any).__staleTimer);
        setFirstGrantProcessedAt(null);
        setETA(null);
        const now = Date.now();
        let completedMessage = "Batch processing completed";
        if (isCredentialsBatch) {
          completedMessage =
            failed > 0
              ? `Credential generation completed with ${failed} failure${
                  failed > 1 ? "s" : ""
                }`
              : "Credentials generated successfully";
        } else if (isLetterBatch) {
          completedMessage =
            failed > 0
              ? `Grant letter processing completed with ${failed} failure${
                  failed > 1 ? "s" : ""
                }`
              : "Grant letters generated successfully";
        } else if (isStateChangeBatch) {
          completedMessage =
            failed > 0
              ? `State change request processed with ${failed} failure${
                  failed > 1 ? "s" : ""
                }`
              : "State change request processed";
        }

        setProcessedGrantLetters({
          batchId: batchId ?? "",
          batchType: batchType ?? "letter",
          total,
          processed,
          failed,
          message: completedMessage,
          lastUpdatedAt: now,
        });

        if (isStateChangeBatch) {
          queryClient.invalidateQueries("allGrants");
          queryClient.refetchQueries("allGrants");
          triggerGrantsRefresh();
        }

        if (clearProgressTimerRef.current) {
          clearTimeout(clearProgressTimerRef.current);
        }
        clearProgressTimerRef.current = setTimeout(
          () => setProcessedGrantLetters(null),
          3000
        );
        return;
      }

      if (batchId) localStorage.setItem(lsBatchKey, batchId);

      let message = "Batch processing in progress";
      if (isCredentialsBatch) {
        message = "Credential generation in progress";
      } else if (isLetterBatch) {
        message = "Grant Letters processing in progress";
      } else if (isStateChangeBatch) {
        message = "State change request in progress";
      }
      const now = Date.now();
      setProcessedGrantLetters({
        batchId: batchId ?? "",
        batchType: batchType ?? "letter",
        total,
        processed,
        failed,
        message,
        lastUpdatedAt: now,
      });
    });
    socket.on("report_status", (data: ReportStatus) => {
      data.reportName = data.reportName?.replace("_", " ") || "Unnamed Report";
      data.message = data.message?.replaceAll("_", " ") || "";
      const toastData = {
        IN_PROGRESS: {
          type: "info" as const,
          message: `${
            data.message ||
            `Report ${data.reportName} generation in progress...`
          }`,
        },
        SUCCESS: {
          type: "success" as const,
          message: `${
            data.message || `Report ${data.reportName} generated successfully.`
          }`,
        },
        FAILURE: {
          type: "error" as const,
          message: `${
            data.message || `Report ${data.reportName} generation failed.`
          }`,
        },
      };
      upsertReport(companyId!, data.reportType, {
        status: data.status,
        data: data.additionalInfo || data.error,
        error: data.additionalInfo,
        reportType: data.reportType,
        updatedAt: data.serverTime,
      });
      if (data.status === "QUEUED") return;
      const toastInfo = toastData[data.status];
      if (!toastInfo) return;
      toast(toastInfo.message, {
        type: toastInfo.type,
        autoClose: 4000,
      });
      queryClient.invalidateQueries("getHistoricReports");
      queryClient.refetchQueries("getHistoricReports");
    });

    socket.on("platform-errors-unread", handleUnread);
    socket.on("grant-errors-unread", handleGrantUnread);
    socket.on("platform-errors-read", handleRead);
    socket.on("platform-errors-all", handleAll);

    return () => {
      if (clearProgressTimerRef.current) {
        clearTimeout(clearProgressTimerRef.current);
      }
      clearTimeout((socket as any).__staleTimer);
      socket.off("platform-errors-unread", handleUnread);
      socket.off("grant-errors-unread", handleGrantUnread);
      socket.off("platform-errors-read", handleRead);
      socket.off("platform-errors-all", handleAll);
      socket.off("report_status");
      socket.off("letter-metrics");
      disconnectSocket();
    };
  }, [companyId]);

  useEffect(() => {
    const socket = getSocketConnection();
    if (tab === "unread") {
      socket.emit("get-unread-platform-errors");
    } else if (tab === "read") {
      socket.emit("get-read-platform-errors");
    } else {
      socket.emit("get-all-platform-errors");
    }
  }, [tab, companyId]);

  const handleTabChange = (newTab: "unread" | "read" | "all") => {
    setTab(newTab);
  };

  const handleClick = (item: PlatformError) => {
    setSelectedNotification(item);
  };

  const handleMarkAsRead = (id: string) => {
    const socket = getSocketConnection();
    socket.emit("mark-platform-error-as-read", { id });
    setSelectedNotification(null);
    socket.emit("get-unread-platform-errors");
  };

  useEffect(() => {
    setSelectedNotification(null);
  }, [tab]);

  const NotificationDetails = ({
    notification,
    onClose,
  }: {
    notification: PlatformError;
    onClose: () => void;
  }) => {
    const reports = HissaReportType;
    const isReport = Object.values(reports).includes(
      notification?.entityType as HissaReportType
    );
    const meta = notification.metadata as any;
    const errors = isReport
      ? JSON.parse(meta || "{}")?.error
      : meta?.errors || {};
    const success = isReport ? {} : meta?.success || {};
    const allIdentifiers = !isReport
      ? Array.from(
          new Set([
            ...Object.keys(errors),
            ...Object.keys(success),
            ...(notification.referenceIds || []),
          ])
        )
      : [];
    return (
      <div className="p-4">
        <button
          className="flex items-center gap-1 mb-2 text-xs text-blue-600 underline hover:text-blue-800"
          onClick={onClose}
        >
          <Icon icon="mdi:chevron-left" className="w-4 h-4" />
          Back
        </button>
        <div className="mb-1 text-base font-semibold">
          {notification.description}
        </div>
        <div className="mb-2 text-xs text-gray-500">
          {notification.createdAt
            ? calculateDateDiffTillNow(notification.createdAt)
            : ""}
        </div>
        {isReport && errors && (
          <div className="mb-4">
            <div className="mb-1 text-sm font-medium">Error Details:</div>
            <div className="p-2 text-xs text-red-600 bg-red-50 rounded whitespace-pre-wrap break-all">
              {getUserFacingError(errors)}
            </div>
          </div>
        )}
        {!isReport && (
          <div className="mb-2">
            <div className="mb-1 text-sm font-medium">Identifiers:</div>
            <ul className="pl-4 list-disc">
              {allIdentifiers.length === 0 && (
                <li className="text-xs text-gray-400">None</li>
              )}
              {allIdentifiers.map((id) => (
                <li key={id} className="mb-1">
                  <button
                    className="font-mono text-xs text-blue-600 underline hover:text-blue-800"
                    onClick={() => {
                      onClose();
                      navigate(`/options/allPlans`);
                    }}
                    type="button"
                  >
                    {id}
                  </button>
                  {errors[id] && (
                    <span className="ml-2 text-xs text-red-500">
                      {getUserFacingError(errors[id])}
                    </span>
                  )}
                  {success[id] && (
                    <span className="ml-2 text-xs text-green-600">
                      {String(success[id])}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {notification.status === "unread" && (
          <button
            className="px-3 py-1 mt-2 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
            onClick={() => handleMarkAsRead(notification.id)}
          >
            Mark as Read
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {processedGrantLetters?.total && !minimized && (
        <div className="fixed bottom-6 right-6 z-[9999] flex items-end justify-end">
          <div className="bg-white rounded-lg shadow-lg p-4 min-w-[380px] flex flex-col items-center border border-gray-200 relative">
            {/* Close and Minimize Icons */}
            <div className="absolute top-2 right-2 flex gap-2">
              <button
                className="text-gray-400 hover:text-gray-700"
                onClick={() => setMinimized(true)}
                title="Minimize"
              >
                <Icon icon="mdi:minus" width={18} height={18} />
              </button>
              <button
                className="text-gray-400 hover:text-gray-700"
                onClick={() => setProcessedGrantLetters(null)}
                title="Close"
              >
                <Icon icon="mdi:close" width={18} height={18} />
              </button>
            </div>
            <span className="mb-2 font-semibold text-gray-700">
              {processedGrantLetters.message}
            </span>
            <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-orange-501 transition-all"
                style={{
                  width: `${Math.round(
                    (processedGrantLetters.processed /
                      processedGrantLetters.total) *
                      100
                  )}%`,
                }}
              />
            </div>
            <span className="text-xs text-gray-500">
              {processedGrantLetters.processed} of {processedGrantLetters.total}{" "}
              processed
              {processedGrantLetters.failed > 0 && (
                <>
                  {" "}
                  |{" "}
                  <span className="text-red-500">
                    {processedGrantLetters.failed} failed
                  </span>
                </>
              )}
            </span>
          </div>
        </div>
      )}
      {processedGrantLetters?.total && minimized && (
        <div className="fixed bottom-6 right-6 z-[9999]">
          <button
            className="bg-white shadow-lg rounded-full p-3 border border-gray-200 flex items-center justify-center hover:bg-gray-50 relative"
            onClick={() => setMinimized(false)}
            title="Show Progress"
            style={{ width: 70, height: 70 }}
          >
            <CircularProgress
              variant="determinate"
              value={Math.round(
                (processedGrantLetters.processed /
                  processedGrantLetters.total) *
                  100
              )}
              size={56}
              thickness={5}
              style={{
                color: "#f97316",
                position: "absolute",
                top: 7,
                left: 7,
              }}
            />
            <span
              className="absolute inset-0 flex items-center justify-center text-xl font-bold"
              style={{ color: "#f97316" }}
            >
              {Math.round(
                (processedGrantLetters.processed /
                  processedGrantLetters.total) *
                  100
              )}
              %
            </span>
          </button>
        </div>
      )}
      <Popover className="relative">
        <PopoverButton
          className={`relative mt-2 ${bellAnimate ? "bell-swing" : ""}`}
        >
          <Icon
            onClick={() => setBellAnimate(false)}
            icon="mdi:bell-outline"
            className={`w-6 h-6 text-gray-600`}
          />
          {notifications.length > 0 && (
            <span
              className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-[3px] flex items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold text-white
              }`}
            >
              {notifications.length}
            </span>
          )}
        </PopoverButton>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel className="absolute right-0 z-50 mt-2 overflow-auto bg-white rounded-lg shadow-xl w-80 max-h-96 ring-1 ring-gray-200 focus:outline-none">
            <div className="flex border-b">
              <button
                className={`flex-1 py-2 text-xs font-semibold ${
                  tab === "unread"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500"
                }`}
                onClick={() => handleTabChange("unread")}
              >
                Unread
              </button>
              <button
                className={`flex-1 py-2 text-xs font-semibold ${
                  tab === "read"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500"
                }`}
                onClick={() => handleTabChange("read")}
              >
                Read
              </button>
              <button
                className={`flex-1 py-2 text-xs font-semibold ${
                  tab === "all"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500"
                }`}
                onClick={() => handleTabChange("all")}
              >
                All
              </button>
            </div>
            {selectedNotification ? (
              <NotificationDetails
                notification={selectedNotification}
                onClose={() => setSelectedNotification(null)}
              />
            ) : notifications.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">Nothing to Show</div>
            ) : (
              <ul className="divide-y">
                {notifications.map((notification, idx) => (
                  <li
                    key={
                      notification.id ||
                      (notification.createdAt
                        ? notification.createdAt.toString()
                        : String(idx)) + (notification.description || "")
                    }
                    onClick={() => handleClick(notification)}
                    className={`flex flex-col gap-2 p-3 text-sm text-gray-800 cursor-pointer ${
                      notification.type === "error"
                        ? "bg-red-50 hover:bg-red-100"
                        : notification.type === "warning"
                        ? "bg-yellow-50 hover:bg-yellow-100"
                        : "bg-green-50 hover:bg-green-100"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon
                        icon={
                          notification.type === "error"
                            ? "mdi:alert-circle-outline"
                            : notification.type === "warning"
                            ? "mdi:alert-outline"
                            : "mdi:information-outline"
                        }
                        className={`h-5 w-5 mt-0.5 ${
                          notification.type === "error"
                            ? "text-red-500"
                            : notification.type === "warning"
                            ? "text-yellow-500"
                            : "text-gray-400"
                        }`}
                      />
                      <div className="flex-1">
                        <div className="font-medium">
                          {notification.description}
                        </div>
                        {notification.referenceIds &&
                          notification.referenceIds?.length > 0 && (
                            <div className="mt-1 text-xs text-gray-700 break-all">
                              {notification.referenceIds}
                            </div>
                          )}
                        <div className="mt-1 text-xs text-gray-500">
                          {notification.createdAt
                            ? calculateDateDiffTillNow(notification.createdAt)
                            : ""}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Popover.Panel>
        </Transition>
      </Popover>
    </>
  );
}

export default NotificationCenter;
